import { useEffect } from 'react'
import { useXR } from '@react-three/xr'

// A grabbed object gets "stuck" to the controller when a per-object pointerup/cancel is dropped,
// leaving @react-three/handle's grab active. select/squeeze events fire at the XRSession level
// (covers controller trigger + grip and hand pinch) and are far more reliable than per-object
// pointer routing — so we track them globally and use "nothing pressed" to cancel orphaned grabs.
let count = 0
let bound: XRSession | undefined
const inc = () => (count += 1)
const dec = () => (count = Math.max(0, count - 1))

function bind(session: XRSession | undefined) {
  if (bound === session) return
  if (bound) {
    bound.removeEventListener('selectstart', inc)
    bound.removeEventListener('selectend', dec)
    bound.removeEventListener('squeezestart', inc)
    bound.removeEventListener('squeezeend', dec)
  }
  bound = session
  count = 0
  if (session) {
    session.addEventListener('selectstart', inc)
    session.addEventListener('selectend', dec)
    session.addEventListener('squeezestart', inc)
    session.addEventListener('squeezeend', dec)
  }
}

/** Mount once inside the scene to keep the global press counter in sync with the active session. */
export function useTrackXRPresses() {
  const session = useXR((s) => s.session)
  useEffect(() => bind(session), [session])
}

/** True while any controller trigger/grip is held or any hand is pinching. */
export function anyXRPressed() {
  return count > 0
}

/** Whether press tracking is bound to a live session (vs desktop pointer / no XR). */
export function inXRSession() {
  return bound != null
}
